/**
 * New Settings UI matching the design system from new-main.html
 * Ensures Fredoka font is loaded
 */
export default class NewSettingsUI {
    private settingsButton: HTMLButtonElement | null = null;
    private settingsModal: HTMLDivElement | null = null;
    private settingsOverlay: HTMLDivElement | null = null;
    private isOpen: boolean = false;

    constructor() {
        this.createSettingsButton();
        this.createSettingsModal();
        this.setupEventListeners();
    }

    private createSettingsButton(): void {
        // Remove old button if it exists
        const oldButton = document.getElementById('settings-toggle');
        if (oldButton) {
            oldButton.style.display = 'none';
        }

        this.settingsButton = document.createElement('button');
        this.settingsButton.id = 'new-settings-toggle';
        this.settingsButton.innerHTML = `
            <svg class="w-8 h-8 text-black z-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="3"
                    d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z">
                </path>
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="3" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z">
                </path>
            </svg>
        `;
        this.settingsButton.style.cssText = `
            position: fixed;
            bottom: 24px;
            right: 24px;
            z-index: 50;
            width: 56px;
            height: 56px;
            background: white;
            border: 4px solid black;
            border-radius: 50%;
            box-shadow: 8px 8px 0px rgba(0, 0, 0, 0.15);
            display: flex;
            align-items: center;
            justify-content: center;
            cursor: pointer;
            transition: transform 0.1s;
        `;
        this.settingsButton.addEventListener('mouseenter', () => {
            if (this.settingsButton) {
                this.settingsButton.style.transform = 'scale(1.1)';
            }
        });
        this.settingsButton.addEventListener('mouseleave', () => {
            if (this.settingsButton) {
                this.settingsButton.style.transform = 'scale(1)';
            }
        });
        document.body.appendChild(this.settingsButton);
    }

    private createSettingsModal(): void {
        // Create overlay
        this.settingsOverlay = document.createElement('div');
        this.settingsOverlay.id = 'new-settings-overlay';
        this.settingsOverlay.style.cssText = `
            position: fixed;
            inset: 0;
            background: rgba(0, 0, 0, 0.4);
            backdrop-filter: blur(4px);
            z-index: 59;
            opacity: 0;
            pointer-events: none;
            transition: opacity 0.5s;
        `;
        this.settingsOverlay.addEventListener('click', () => this.toggle());

        // Create modal container
        this.settingsModal = document.createElement('div');
        this.settingsModal.id = 'new-settings-modal';
        this.settingsModal.style.cssText = `
            position: fixed;
            inset: 0;
            z-index: 60;
            display: flex;
            align-items: flex-end;
            justify-content: center;
            pointer-events: none;
            transition: transform 0.5s;
            transform: translateY(100%);
        `;

        // Create modal content
        const modalContent = document.createElement('div');
        modalContent.style.cssText = `
            position: relative;
            width: 90%;
            max-width: 448px;
            height: 80%;
            background: #8B5E3C;
            border-radius: 48px 48px 0 0;
            padding: 16px;
            padding-bottom: 0;
            box-shadow: 0 25px 50px -12px rgba(0, 0, 0, 0.25);
            transform: translateY(40px);
            transition: transform 0.5s;
            display: flex;
            flex-direction: column;
            align-items: center;
            pointer-events: auto;
        `;

        // Create handle
        const handle = document.createElement('div');
        handle.style.cssText = `
            position: absolute;
            top: -32px;
            left: 50%;
            transform: translateX(-50%);
            width: 128px;
            height: 64px;
            background: #d1d5db;
            border-radius: 8px 8px 0 0;
            border: 4px solid black;
            display: flex;
            align-items: center;
            justify-content: center;
            box-shadow: 0 10px 15px -3px rgba(0, 0, 0, 0.1);
            z-index: 20;
        `;
        const handleBar = document.createElement('div');
        handleBar.style.cssText = `
            width: 96px;
            height: 4px;
            background: rgba(0, 0, 0, 0.2);
            border-radius: 9999px;
        `;
        handle.appendChild(handleBar);
        modalContent.appendChild(handle);

        // Create inner content
        const innerContent = document.createElement('div');
        innerContent.style.cssText = `
            background: #Fdfbf7;
            width: 100%;
            height: 100%;
            border-radius: 32px 32px 0 0;
            padding: 32px;
            padding-top: 48px;
            position: relative;
            box-shadow: inset 0 0 20px rgba(0, 0, 0, 0.05);
            overflow-y: auto;
        `;

        // Title
        const title = document.createElement('h2');
        title.textContent = 'CONTROLS';
        title.style.cssText = `
            text-align: center;
            margin-bottom: 32px;
            font-size: 36px;
            font-weight: 900;
            color: #1f2937;
            letter-spacing: -0.025em;
            text-decoration: underline;
            text-decoration-color: #FF6B6B;
            text-decoration-thickness: 4px;
            text-underline-offset: 4px;
            font-family: 'Fredoka', sans-serif;
        `;
        innerContent.appendChild(title);

        // Settings content
        const settingsContent = document.createElement('div');
        settingsContent.style.cssText = `
            display: flex;
            flex-direction: column;
            gap: 24px;
        `;

        // Music Volume
        const musicVolume = this.createVolumeSlider('Music Volume', 70, '#4ECDC4');
        settingsContent.appendChild(musicVolume);

        // SFX Volume
        const sfxVolume = this.createVolumeSlider('SFX Volume', 40, '#FFE66D');
        settingsContent.appendChild(sfxVolume);

        // Show Player Names toggle
        const playerNamesToggle = this.createToggle('Show Player Names', true);
        settingsContent.appendChild(playerNamesToggle);

        // Vibration toggle
        const vibrationToggle = this.createToggle('Vibration / Haptics', false);
        settingsContent.appendChild(vibrationToggle);

        // Account section
        const accountSection = document.createElement('div');
        accountSection.style.cssText = `
            padding-top: 24px;
            border-top: 2px dashed #d1d5db;
            margin-top: 24px;
        `;
        const accountLabel = document.createElement('label');
        accountLabel.textContent = 'Account';
        accountLabel.style.cssText = `
            font-weight: bold;
            color: #6b7280;
            font-size: 12px;
            letter-spacing: 0.1em;
            text-transform: uppercase;
            margin-bottom: 8px;
            display: block;
            font-family: 'Fredoka', sans-serif;
        `;
        accountSection.appendChild(accountLabel);
        const logoutButton = document.createElement('button');
        logoutButton.innerHTML = '<span>??</span> LOG OUT';
        logoutButton.style.cssText = `
            width: 100%;
            background: #FF6B6B;
            color: white;
            font-weight: 900;
            padding: 12px;
            border-radius: 12px;
            border: 4px solid black;
            box-shadow: 4px 4px 0 black;
            transition: all 0.1s;
            display: flex;
            align-items: center;
            justify-content: center;
            gap: 8px;
            font-family: 'Fredoka', sans-serif;
            cursor: pointer;
        `;
        logoutButton.addEventListener('mousedown', () => {
            logoutButton.style.transform = 'translate(1px, 1px)';
            logoutButton.style.boxShadow = 'none';
        });
        logoutButton.addEventListener('mouseup', () => {
            logoutButton.style.transform = 'translate(0, 0)';
            logoutButton.style.boxShadow = '4px 4px 0 black';
        });
        accountSection.appendChild(logoutButton);
        settingsContent.appendChild(accountSection);

        innerContent.appendChild(settingsContent);

        // Close button
        const closeButtonContainer = document.createElement('div');
        closeButtonContainer.style.cssText = `
            position: sticky;
            bottom: 0;
            background: linear-gradient(to top, #Fdfbf7, #Fdfbf7, transparent);
            padding-top: 32px;
            padding-bottom: 16px;
            margin-top: 16px;
            display: flex;
            justify-content: center;
        `;
        const closeButton = document.createElement('button');
        closeButton.textContent = 'Close';
        closeButton.style.cssText = `
            background: white;
            color: black;
            font-weight: 900;
            padding: 12px 48px;
            border-radius: 9999px;
            border: 4px solid black;
            box-shadow: 0 6px 0 rgba(0, 0, 0, 0.2);
            transition: all 0.1s;
            text-transform: uppercase;
            letter-spacing: 0.1em;
            font-size: 14px;
            font-family: 'Fredoka', sans-serif;
            cursor: pointer;
        `;
        closeButton.addEventListener('mouseenter', () => {
            closeButton.style.transform = 'translateY(-4px)';
        });
        closeButton.addEventListener('mouseleave', () => {
            closeButton.style.transform = 'translateY(0)';
        });
        closeButton.addEventListener('mousedown', () => {
            closeButton.style.transform = 'translateY(1px)';
            closeButton.style.boxShadow = 'none';
        });
        closeButton.addEventListener('mouseup', () => {
            closeButton.style.transform = 'translateY(0)';
            closeButton.style.boxShadow = '0 6px 0 rgba(0, 0, 0, 0.2)';
        });
        closeButton.addEventListener('click', () => this.toggle());
        closeButtonContainer.appendChild(closeButton);
        innerContent.appendChild(closeButtonContainer);

        modalContent.appendChild(innerContent);
        this.settingsModal.appendChild(modalContent);
        document.body.appendChild(this.settingsOverlay);
        document.body.appendChild(this.settingsModal);
    }

    private createVolumeSlider(label: string, value: number, color: string): HTMLDivElement {
        const container = document.createElement('div');
        container.style.cssText = `
            display: flex;
            flex-direction: column;
            gap: 8px;
        `;

        const labelEl = document.createElement('label');
        labelEl.textContent = label;
        labelEl.style.cssText = `
            font-weight: bold;
            color: #6b7280;
            font-size: 12px;
            letter-spacing: 0.1em;
            text-transform: uppercase;
            font-family: 'Fredoka', sans-serif;
        `;
        container.appendChild(labelEl);

        const sliderContainer = document.createElement('div');
        sliderContainer.style.cssText = `
            background: #e5e7eb;
            height: 16px;
            border-radius: 9999px;
            width: 100%;
            position: relative;
            border: 2px solid black;
        `;

        const fill = document.createElement('div');
        fill.style.cssText = `
            position: absolute;
            top: 0;
            left: 0;
            height: 100%;
            background: ${color};
            border-radius: 9999px 0 0 9999px;
            width: ${value}%;
        `;
        sliderContainer.appendChild(fill);

        const thumb = document.createElement('div');
        thumb.style.cssText = `
            position: absolute;
            top: 50%;
            width: 32px;
            height: 32px;
            background: white;
            border: 4px solid black;
            border-radius: 50%;
            box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
            cursor: pointer;
            left: ${value}%;
            transform: translate(-50%, -50%);
            transition: transform 0.1s;
        `;
        thumb.addEventListener('mouseenter', () => {
            thumb.style.transform = 'translate(-50%, -50%) scale(1.1)';
        });
        thumb.addEventListener('mouseleave', () => {
            thumb.style.transform = 'translate(-50%, -50%) scale(1)';
        });
        sliderContainer.appendChild(thumb);

        container.appendChild(sliderContainer);
        return container;
    }

    private createToggle(label: string, enabled: boolean): HTMLDivElement {
        const container = document.createElement('div');
        container.style.cssText = `
            display: flex;
            align-items: center;
            justify-content: space-between;
            background: white;
            border: 2px solid #e5e7eb;
            padding: 12px;
            border-radius: 12px;
        `;

        const labelEl = document.createElement('span');
        labelEl.textContent = label;
        labelEl.style.cssText = `
            font-weight: bold;
            color: #1f2937;
            font-family: 'Fredoka', sans-serif;
        `;
        container.appendChild(labelEl);

        const toggle = document.createElement('div');
        toggle.style.cssText = `
            width: 64px;
            height: 32px;
            background: ${enabled ? '#82c9a5' : '#d1d5db'};
            border-radius: 9999px;
            border: 2px solid black;
            position: relative;
            cursor: pointer;
            box-shadow: inset 0 2px 4px rgba(0, 0, 0, 0.1);
        `;

        const thumb = document.createElement('div');
        thumb.style.cssText = `
            position: absolute;
            width: 20px;
            height: 20px;
            background: white;
            border: 2px solid black;
            border-radius: 50%;
            box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
            top: 4px;
            ${enabled ? 'right: 4px;' : 'left: 4px;'}
            transition: left 0.2s, right 0.2s;
        `;
        toggle.appendChild(thumb);

        toggle.addEventListener('click', () => {
            enabled = !enabled;
            toggle.style.background = enabled ? '#82c9a5' : '#d1d5db';
            thumb.style.left = enabled ? 'auto' : '4px';
            thumb.style.right = enabled ? '4px' : 'auto';
        });

        container.appendChild(toggle);
        return container;
    }

    private setupEventListeners(): void {
        if (this.settingsButton) {
            this.settingsButton.addEventListener('click', () => this.toggle());
        }
    }

    public toggle(): void {
        this.isOpen = !this.isOpen;

        if (this.settingsModal && this.settingsOverlay) {
            if (this.isOpen) {
                this.settingsModal.style.transform = 'translateY(0)';
                this.settingsOverlay.style.opacity = '1';
                this.settingsOverlay.style.pointerEvents = 'auto';
                const modalContent = this.settingsModal.querySelector('div') as HTMLElement;
                if (modalContent) {
                    modalContent.style.transform = 'translateY(0)';
                }
            } else {
                this.settingsModal.style.transform = 'translateY(100%)';
                this.settingsOverlay.style.opacity = '0';
                this.settingsOverlay.style.pointerEvents = 'none';
                const modalContent = this.settingsModal.querySelector('div') as HTMLElement;
                if (modalContent) {
                    modalContent.style.transform = 'translateY(40px)';
                }
            }
        }
    }
}

